import { useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import PageSchema from "@/components/PageSchema";
import LanguageSwitchLink from "@/components/LanguageSwitchLink";
import PricingDisclaimer from "@/components/PricingDisclaimer";
import { setPageMeta, setHreflang } from "@/lib/seo";

const EN_URL = "/trailer-guides/ownership-costs";
const ES_URL = "/es/costos-de-tener-un-remolque";

export default function CostosDeTenerUnRemolquePage() {
  useEffect(() => {
    document.documentElement.lang = "es";
    setPageMeta(
      "Costos Reales de Tener un Remolque | Juan The Trailer Guy",
      "El precio de etiqueta es solo el comienzo. Te explicamos seguro, mantenimiento, costos de arranque y la economía real de tener un remolque.",
      ES_URL,
    );
    const cleanup = setHreflang([
      { lang: "en-US", href: EN_URL },
      { lang: "es-US", href: ES_URL },
      { lang: "x-default", href: EN_URL },
    ]);
    return () => {
      document.documentElement.lang = "en";
      cleanup();
    };
  }, []);

  return (
    <main className="bg-background text-foreground">
      <PageSchema
        name="Costos Reales de Tener un Remolque"
        description="El precio de etiqueta es solo el comienzo. Seguro, mantenimiento, costos de arranque y la economía real de tener un remolque."
      />
      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
          <Breadcrumbs
            variant="dark"
            className="mb-5"
            items={[{ name: "Inicio", href: "/" }, { name: "Guías Educativas" }, { name: "Costos de Tener un Remolque" }]}
          />
          <p className="text-sm uppercase tracking-[0.2em] text-red-400 font-semibold">Propiedad del Remolque</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-black uppercase leading-tight">
            Costos Reales de Tener un Remolque
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-zinc-300">
            El precio de etiqueta es solo el comienzo. Estas son las guías sobre seguro, costos de
            arranque, presupuesto de mantenimiento y la economía real de los negocios que dependen de un
            remolque — hotshot, volteo y roll-off.
          </p>
          <div className="mt-6">
            <LanguageSwitchLink href={EN_URL} label="Read this guide in English" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-14 text-lg leading-8 text-muted-foreground">
        <PricingDisclaimer className="mb-8" />

        <h2 className="text-2xl font-black uppercase text-foreground">1. El Seguro</h2>
        <p className="mt-4">
          Muchos compradores primerizos olvidan presupuestar el seguro del remolque. Si lo usas para tu
          negocio, probablemente necesitas una póliza comercial, que suele costar más que una póliza
          personal. El costo depende del valor del remolque, cómo lo usas, y tu historial. Pide
          cotizaciones de varias aseguradoras antes de comprar, para que el costo del seguro no te tome
          por sorpresa.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">2. Mantenimiento Rutinario</h2>
        <p className="mt-4">
          Llantas, rodamientos, frenos, luces y grasa — todo esto cuesta dinero con el tiempo, pero cuesta
          mucho menos que ignorarlo. Presupuesta mantenimiento preventivo regular en lugar de esperar a
          que algo falle. Revisa nuestra{" "}
          <Link to="/es/mantenimiento-de-remolques" className="font-semibold text-primary underline">
            guía de mantenimiento de remolques
          </Link>{" "}
          para saber qué revisar y con qué frecuencia.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">3. Costos de Arranque</h2>
        <p className="mt-4">
          Además del precio del remolque, considera: registro y placas, inspección (si tu estado lo
          requiere), llantas de repuesto, cadenas de seguridad adecuadas, y accesorios como lonas o
          rampas. Estos costos pueden sumar varios cientos de dólares antes de que el remolque esté listo
          para trabajar.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">4. Depreciación y Reventa</h2>
        <p className="mt-4">
          Los remolques generalmente mantienen su valor mejor que muchos vehículos, pero aun así se
          deprecian. Un remolque bien mantenido, con historial claro de mantenimiento, se vende mejor y
          más rápido cuando llegue el momento de actualizar o vender.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">5. La Economía de Usarlo para el Trabajo</h2>
        <p className="mt-4">
          Si usas el remolque para generar ingresos (hotshot, volteo, limpieza, acarreo), piensa en el
          costo total de propiedad por trabajo o por semana, no solo en el pago mensual. Eso incluye
          combustible extra, desgaste de la troca, seguro comercial y mantenimiento — todo entra en el
          cálculo de si el remolque te está generando ganancia real.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">6. No Olvides la Capacidad Correcta</h2>
        <p className="mt-4">
          Comprar una capacidad más baja de la que necesitas parece ahorrar dinero al inicio, pero termina
          costando más en desgaste, reparaciones y tiempo perdido. Revisa nuestra{" "}
          <Link to="/es/gvwr-capacidad-de-carga" className="font-semibold text-primary underline">
            guía de GVWR y capacidad de carga
          </Link>{" "}
          antes de decidir el tamaño del remolque.
        </p>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Sigue Aprendiendo</h2>
          <div className="mt-6 text-primary font-semibold space-y-2">
            <Link to="/es/mantenimiento-de-remolques" className="block hover:underline">→ Mantenimiento de Remolques</Link>
            <Link to="/es/gvwr-capacidad-de-carga" className="block hover:underline">→ GVWR y Capacidad de Carga</Link>
            <Link to="/es/remolques-de-volteo" className="block hover:underline">→ Tráileres de Volteo Explicadas</Link>
            <Link to="/es/remolque-volteo-14k-vs-16k" className="block hover:underline">→ Tráiler de Volteo 14K vs 16K</Link>
            <Link to="/es/licencia-cdl-remolques" className="block hover:underline">→ CDL y Remolcado</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
